import LiveLessonsList from "@/pages/LiveLessonsList";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import SubscriptionPlans from "@/pages/SubscriptionPlans";
import WalletPage from "@/pages/WalletPage";
import { isElectron, isQashqoolMobile } from "@/scripts/environmentDetector";
import { getHostname } from "@/scripts/getHostname";
import { setHostname } from "@/store/slices/hostnameSlice";
import { useEffect } from "react";
import { BrowserRouter, HashRouter, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { remoteConfig } from "../firebaseConfig";
import "../firebaseConfig.ts";
import { endpoints } from "../hooks/endpoints";
import useCheckForUpdates from "../hooks/useCheckForUpdates";
import useNotifications from "../hooks/useNotifications";
import usePWA from "../hooks/usePWA";
import { isProSubscription, isSubscribed, isTeacher } from "../hooks/utils";
import { setTeacherColorScheme } from "../scripts/setTeacherColorScheme";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import AccountPage from "./account/AccountPage";
import Animation from "./Animation";
import CompleteInfo from "./complete-info/CompleteInfo";
import { dashboardItems } from "./dashboard/DashboardComponents/DashboardItems";
import DashItemWrapper from "./dashboard/DashItemWrapper";
import MainDashPage from "./dashboard/MainDashPage/MainDashPage";
import DesktopLoginPage from "./Desktop/DesktopLoginPage";
import MyTeachers from "./Desktop/MyTeachers";
import DynamicCategoriesRouterComponent from "./DynamicCategoriesRouterComponent";
import Home from "./home/Home";
import PasswordReset from "./home/PasswordReset";
import InstallAppPage from "./InstallAppPage/InstallAppPage";
import LiveSession from "./lesson/LiveSession/LiveSession";
import ModalStack from "./ModalStack";
import MultipleDeviceNotice from "./MultipleDeviceNotice";
import Footer from "./nav-footer/Footer";
import Navbar from "./nav-footer/Navbar";
import NotificationsDrawer from "./notifications/NotificationsDrawer";
import Invoice from "./orders/Invoice";
import Invoices from "./orders/Invoices";
import LandingPage from "./qashqool/LandingPage";
import Grade from "./quiz/Grade";
import Grades from "./quiz/Grades";
import TutorRegistration from "./TutorRegistration/TutorRegistration";

function App() {
  const dispatch = useAppDispatch();
  dispatch(setHostname(getHostname()));
  const { portal, user, userProfileData, isPortalPending, isUserPending } =
    useAppSelector((state) => state.store);
  const modals = useAppSelector((state) => state.modals.modals);
  const hostname = useAppSelector((state) => state.hostname.hostname);

  // Use HashRouter for Electron to avoid Windows drive letter path issues
  const Router = (isElectron() || isQashqoolMobile()) ? HashRouter : BrowserRouter;

  useCheckForUpdates({ remoteConfig: remoteConfig, key: "app_version" });
  useNotifications();

  useEffect(() => {
    console.log("Current Portal: ", portal);
  }, [portal]);

  useEffect(() => {
    console.log("Current User: ", user);
  }, [user]);

  // return (
  //   <div className="closed-for-updates container mx-5 my-2">
  //     <div className="d-flex">
  //       <img src='https://eliteacademymedia.s3.us-east-1.amazonaws.com/qashqool_assets/media/Operating+system+upgrade-pana.svg' alt="updating" className="mx-auto" />
  //     </div>
  //     <div className="d-flex">
  //       <p className="h1 mx-auto">الموقع مغلق للتحديثات</p>
  //     </div>
  //     <div className="d-flex">
  //       <p className="h2 mx-auto">أعد المحاولة بعد ساعة</p>
  //     </div>
  //   </div>
  // );

  const checkVersionAndSW = async () => {
    try {
      const res = await fetch(
        `${endpoints.checkVersionAndSW}?rand_key=${Math.random()}`
      );
      const data: {
        current_app_version: string;
        remove_service_worker: boolean;
      } = (await res.json()).data;

      const currentAppVersionFromServerLocalStorage = localStorage.getItem(
        "current_app_version_from_server"
      );

      if (data?.remove_service_worker) {
        console.log("removing service worker");
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          registrations?.forEach((registration) => {
            registration?.unregister();
          });
        });
      }

      if (
        data?.current_app_version &&
        data?.current_app_version !== currentAppVersionFromServerLocalStorage
      ) {
        console.log("updating app to latest version");
        caches.keys().then((names) => {
          names.forEach(async (name) => {
            await caches.delete(name);
          });
          localStorage.setItem(
            "current_app_version_from_server",
            data?.current_app_version.toString()
          );
          location.reload();
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    setTeacherColorScheme(getHostname());
    checkVersionAndSW();
  }, []);

  const { isAppInstalled } = usePWA();

  const isQashqoolLandingPage = getHostname().split(".")[0] === "qashqool";

  if (isQashqoolLandingPage) {
    return (
      <Router>
        <ModalStack />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/registration" element={<TutorRegistration />} />
        </Routes>
      </Router>
    );
  }

  // if (isPortalPending || isUserPending) {
  //   return (
  //     <main className="min-h-dvh flex flex-col w-full mobile-nav bg-gradient-to-br from-yellow-50 via-(--secondary-color)/10 to-yellow-50">
  //       <Spinner variant="default" />
  //     </main>
  //   );
  // }

  if (userProfileData?.data_incomplete) {
    return (
      <Router>
        <main className="min-h-dvh flex flex-col w-full mobile-nav bg-gradient-to-br from-yellow-50 via-(--secondary-color)/10 to-yellow-50">
          <Navbar />
          <Toaster />
          <Animation>
            <div
              className={`content ${
                modals.length > 0 ? "modal-open" : ""
              } bg-[#f7f6f4]`}
              style={{
                paddingBottom: isAppInstalled ? "5rem" : "1rem",
              }}
            >
              <Routes>
                <Route
                  path="*"
                  element={<AccountPage dataNotCompleted={true} />}
                />
              </Routes>
            </div>
          </Animation>
        </main>
      </Router>
    );
  }

  if ((isElectron() || isQashqoolMobile()) && hostname === "") {
    // if (true) {
    return (
      <Router>
        <main className="min-h-dvh flex flex-col w-full mobile-nav bg-gradient-to-br from-yellow-50 via-(--secondary-color)/10 to-yellow-50">
          <ModalStack />
          <Navbar />
          <NotificationsDrawer />
          <Toaster />
          <Animation>
            <div
              className={`content ${modals.length > 0 ? "modal-open" : ""}`}
              style={{
                paddingBottom: isAppInstalled ? "5rem" : "1rem",
              }}
            >
              <Routes>
                <Route path="/password-reset" element={<PasswordReset />} />
                <Route
                  path="*"
                  element={user ? <MyTeachers /> : <DesktopLoginPage />}
                />
              </Routes>
            </div>
          </Animation>
        </main>
      </Router>
    );
  }

  return (
    <Router>
      <ModalStack />
      <main className="min-h-dvh flex flex-col w-full mobile-nav bg-gradient-to-br from-yellow-50 via-(--secondary-color)/10 to-yellow-50">
        <Navbar />
        <NotificationsDrawer />
        <Toaster />
        <Animation>
          <div
            className={`content ${
              modals.length > 0 ? "modal-open" : ""
            } bg-[#f7f6f4]`}
            style={{
              paddingBottom: isAppInstalled ? "5rem" : "1rem",
            }}
          >
            <Routes>
              {isTeacher() && (
                <>
                  <Route
                    path="/dashboard"
                    element={
                      <DashItemWrapper activeItem="main">
                        <MainDashPage />
                      </DashItemWrapper>
                    }
                  />
                  {dashboardItems.map((item) => {
                    const isAllowed =
                      item.isFree ||
                      (item.plan === "basic" && isSubscribed()) ||
                      (item.plan === "pro" && isProSubscription());
                    if (isAllowed)
                      return (
                        <Route
                          path={`/dashboard/${item.slug}`}
                          element={
                            <DashItemWrapper activeItem={item.slug}>
                              {item.component}
                            </DashItemWrapper>
                          }
                        />
                      );
                  })}
                </>
              )}
              {!!user && (
                <>
                  <Route path="/account" element={<AccountPage />} />
                  <Route path="/live" element={<LiveLessonsList />} />
                  <Route path="/grades/:id" element={<Grade />} />
                  <Route path="/grades" element={<Grades />} />
                  <Route path="/purchases/:id" element={<Invoice />} />
                  <Route path="/purchases" element={<Invoices />} />
                  <Route
                    path="/:schoolgrade/:group/live/:session"
                    element={<LiveSession />}
                  />
                  <Route path="/complete-info" element={<CompleteInfo />} />
                  <Route
                    path="/multiple-device-notice"
                    element={<MultipleDeviceNotice />}
                  />
                  <Route
                    path="/subscription-plans"
                    element={<SubscriptionPlans />}
                  />
                </>
              )}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/password-reset" element={<PasswordReset />} />
              <Route path="/install" element={<InstallAppPage />} />
              {["azhar-academy.com", "localhost"].includes(hostname) && (
                <Route path="/wallet" element={<WalletPage />} />
              )}
              <Route
                path="/index.html"
                element={user ? <DynamicCategoriesRouterComponent /> : <Home />}
              />
              <Route
                path="/home"
                element={user ? <DynamicCategoriesRouterComponent /> : <Home />}
              />
              <Route
                path="/"
                element={user ? <DynamicCategoriesRouterComponent /> : <Home />}
              />
              <Route path="*" element={<DynamicCategoriesRouterComponent />} />
            </Routes>
          </div>
        </Animation>
        <Footer />
      </main>
    </Router>
  );
}

export default App;
